// /cabinet/edu?child=<uuid> — анкета «Образование за рубежом».
//
// Обвязка вокруг EduQuestionnaire: загрузка состояния, согласие, черновик.
// Подбора и отчёта здесь нет — это следующие шаги.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { loginRedirect } from "@/lib/return-to";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import {
  getEduProfile,
  grantEduConsent,
  saveEduProfile,
} from "@/lib/edu-profile.functions";
import { generateEduReport, getEduReport } from "@/lib/edu-report.functions";
import { eduScreen, retakeFromSearch, REPORT_POLL_ATTEMPTS, REPORT_POLL_MS } from "@/edu/flow";
import { getEduAvailability } from "@/lib/edu-availability";
import { buildBlocks, type Answers, type AnswerValue } from "@/edu/questionnaire";
import { EduQuestionnaire } from "@/components/edu/EduQuestionnaire";
import { EduAccessError } from "@/components/edu/EduAccessError";
import { EduClosedNotice } from "@/components/edu/EduClosedNotice";

export const Route = createFileRoute("/cabinet_/edu")({
  validateSearch: (search: Record<string, unknown>): { child?: string; again?: string } => ({
    child: typeof search.child === "string" ? search.child : undefined,
    // «Заново» живёт в адресе, а не только в памяти вкладки: перезагрузка
    // посреди повторного прохода не должна выкидывать обратно на отчёт.
    //
    // СРАВНЕНИЕ ЧЕРЕЗ String, И ЭТО НЕ ПЕРЕСТРАХОВКА.
    //
    // Роутер разбирает строку запроса по-JSON'овски: `?again=1` приезжает сюда
    // ЧИСЛОМ 1, а не строкой «1». Сравнение `=== "1"` давало false, валидатор
    // возвращал undefined, роутер считал параметр лишним и отвечал 307-м
    // редиректом на тот же адрес БЕЗ него. Дальше всё работало как задумано и
    // потому незаметно: повторного прохода нет, отчёт есть — уводим на отчёт.
    //
    // Измерено на бою, а не выведено:
    //   /cabinet/edu?child=X&again=1     → 307 → /cabinet/edu?child=X
    //   /cabinet/edu?child=X&again=%221%22 → 200, параметр цел
    //
    // Цена дефекта: «Изменить ответы» не работала ни разу с 7 августа, и рядом
    // с ней стоял текст, обещающий, что она работает.
    again: retakeFromSearch(search.again),
  }),
  head: () => ({
    meta: [
      { title: "Образование за рубежом — Yolutap" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EduProfilePage,
});

function EduProfilePage() {
  const { child: childId, again } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const fetchProfile = useServerFn(getEduProfile);
  const putProfile = useServerFn(saveEduProfile);
  const putConsent = useServerFn(grantEduConsent);
  const makeReport = useServerFn(generateEduReport);
  const checkOpen = useServerFn(getEduAvailability);
  const fetchReport = useServerFn(getEduReport);

  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        // Язык и путь возврата едут на вход: после письма человек вернётся сюда.
        navigate(loginRedirect());
        return;
      }
      setAuthChecked(true);
    });
  }, [navigate]);

  // Модуль закрыт, пока справочник стран не опубликован: см. edu-availability.
  const moduleOpen = useQuery({
    queryKey: ["edu-open"],
    enabled: authChecked,
    queryFn: () => checkOpen({}),
  });
  const isOpen = moduleOpen.data?.open === true;

  const state = useQuery({
    queryKey: ["edu-profile", childId],
    enabled: Boolean(authChecked && isOpen && childId),
    queryFn: () => fetchProfile({ data: { childId: childId as string } }),
  });

  const [answers, setAnswers] = useState<Answers>({});
  const [blockIndex, setBlockIndex] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);
  const [budgetSkipped, setBudgetSkipped] = useState(false);
  // Анкета дозаполнена в этой сессии. Отдельно от статуса в базе: пока запрос
  // профиля не обновился, экран уже не должен показывать вопросы.
  const [justCompleted, setJustCompleted] = useState(false);
  const [generateFailed, setGenerateFailed] = useState(false);
  const [attempts, setAttempts] = useState(0);
  // Повторный проход начинается из адреса и заканчивается отправкой анкеты.
  const [retaking, setRetaking] = useState(again === "1");
  /**
   * Версия отчёта, с которой семья вошла в повторный проход.
   *
   * Нужна, чтобы отличить «отчёт есть» от «отчёт по НОВЫМ ответам появился».
   * Без неё после отправки экран мгновенно уводил бы на прежний документ:
   * старый отчёт лежит и никуда не денется, значит «отчёт есть» истинно с
   * первой секунды повторного прохода.
   */
  const [baseVersion, setBaseVersion] = useState<number | null>(null);

  // Черновик подхватывается при загрузке — «к анкете можно вернуться».
  useEffect(() => {
    const p = state.data?.profile;
    if (!p) return;
    setAnswers((prev) => (Object.keys(prev).length ? prev : (p.answers ?? {})));
    setBudgetSkipped(Boolean(p.budget_skipped));
    if (p.status === "completed") setJustCompleted(true);
  }, [state.data?.profile]);

  const profileCompleted =
    justCompleted || state.data?.profile?.status === "completed";

  // Отчёт спрашивается ЗДЕСЬ. Раньше маршрут его не запрашивал вовсе: экран
  // обещал «меньше минуты» и не имел способа узнать, что минута прошла.
  const report = useQuery({
    queryKey: ["edu-report", childId],
    enabled: Boolean(authChecked && isOpen && childId && profileCompleted),
    queryFn: () => fetchReport({ data: { childId: childId as string } }),
    // Пока отчёта нет — переспрашиваем. Ожидание конечно: счётчик попыток
    // ниже переводит экран в отказ, а не оставляет крутиться.
    refetchInterval: (q) =>
      (q.state.data as { report?: unknown } | undefined)?.report ? false : REPORT_POLL_MS,
  });
  const reportVersion = report.data?.report?.version ?? null;

  // Запоминаем версию на входе в повторный проход — один раз, при первом же
  // ответе запроса. Через состояние, а не через ref: перезагрузка страницы
  // посреди прохода должна дать тот же результат, что и вход по кнопке.
  useEffect(() => {
    if (!retaking || baseVersion !== null || reportVersion === null) return;
    setBaseVersion(reportVersion);
  }, [retaking, baseVersion, reportVersion]);

  // «Отчёт есть» на повторном проходе означает НОВЫЙ отчёт. Прежний остаётся
  // на месте, и принять его за результат новых ответов — значит показать
  // семье чужой документ и назвать его её.
  const reportExists =
    reportVersion !== null && (baseVersion === null || reportVersion > baseVersion);

  useEffect(() => {
    if (!profileCompleted || reportExists) return;
    if (report.isFetching) return;
    setAttempts((n) => (n >= REPORT_POLL_ATTEMPTS ? n : n + 1));
  }, [profileCompleted, reportExists, report.isFetching, report.dataUpdatedAt]);

  const screen = eduScreen({
    profileCompleted: Boolean(profileCompleted),
    reportExists,
    generateFailed,
    attempts,
    again: retaking,
  });

  // Единственная дверь к отчёту, которой раньше не было ни одной.
  useEffect(() => {
    if (screen.kind !== "report" || !childId) return;
    navigate({ to: "/cabinet/edu-report", search: { child: childId }, replace: true });
  }, [screen.kind, childId, navigate]);

  /**
   * Повторный проход закончен — анкета отправлена.
   *
   * Снимается и состояние, и параметр в адресе: оставить `again=1` в строке
   * значило бы, что обновление страницы на экране расчёта начинает анкету
   * заново поверх только что отправленных ответов.
   */
  const endRetake = () => {
    if (!retaking) return;
    setRetaking(false);
    navigate({ to: "/cabinet/edu", search: { child: childId }, replace: true });
  };

  const retry = () => {
    setGenerateFailed(false);
    setAttempts(0);
    makeReport({ data: { childId: childId as string } })
      .then(() => report.refetch())
      .catch(() => setGenerateFailed(true));
  };

  const blocks = useMemo(
    // Страны берутся из справочника: список, переписанный руками, разъехался
    // с is_active — Голландию включили, а выбрать её было нельзя.
    () => buildBlocks(state.data?.snapshotDirections ?? [], state.data?.countries ?? []),
    [state.data?.snapshotDirections, state.data?.countries],
  );

  const save = useMutation({
    mutationFn: (opts: { complete?: boolean; skipped?: boolean; next?: Answers }) =>
      putProfile({
        data: {
          childId: childId as string,
          answers: (opts.next ?? answers) as Record<string, string | string[] | null>,
          budgetSkipped: opts.skipped ?? budgetSkipped,
          snapshotId: state.data?.snapshotId ?? null,
          complete: Boolean(opts.complete),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edu-profile", childId] }),
  });

  const consent = useMutation({
    mutationFn: () => putConsent({ data: { childId: childId as string, locale: "ru" } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edu-profile", childId] }),
  });

  if (!childId) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">
        Не указан ребёнок. Откройте анкету из кабинета.
      </div>
    );
  }
  if (!authChecked || moduleOpen.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }
  if (!isOpen) return <EduClosedNotice />;
  if (state.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }
  {
    /* Отказ прав виден человеку, а не только в теле ответа. Учётка учителя
       жала «Начать анкету» для чужого ребёнка и не получала на экране НИЧЕГО:
       для неё это выглядело поломкой, для нас — здоровым трафиком. */
  }
  if (state.isError) {
    return <EduAccessError error={state.error} childId={childId} />;
  }
  if (state.data?.tableMissing) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900">
          Таблица <code>edu_profiles</code> ещё не создана — миграция
          <code> 20260813120000_edu_profiles.sql </code> накатывается вручную.
        </div>
      </div>
    );
  }

  // Отчёт есть — уже уводим на него эффектом выше; здесь только не мигать
  // анкетой на кадр перехода.
  if (screen.kind === "report") {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Открываем отчёт…</div>;
  }

  if (screen.kind === "computing") {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-2xl border border-yt-line bg-background p-6">
          <p className="text-base font-medium">Считаем варианты</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Это занимает несколько секунд. Экран сменится сам — обновлять страницу не нужно.
          </p>
        </div>
      </div>
    );
  }

  if (screen.kind === "failed") {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6">
          <p className="text-base font-medium text-rose-800">
            {screen.reason === "timeout"
              ? "Расчёт не завершился"
              : "Расчёт не удался"}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            {/* Молчаливое ожидание было бы удобнее нам и хуже человеку: он ждал
                бы того, чего не будет. Анкета сохранена — терять её не
                придётся в любом случае. */}
            Ваши ответы сохранены, ничего не потеряно. Попробуйте посчитать ещё раз — если не
            выйдет и со второго раза, напишите нам, и мы разберёмся.
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const isLastBlock = blockIndex >= blocks.length - 1;

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      {/* Повторный проход обязан сказать вслух, что происходит с прежним
          отчётом. Иначе кнопку «пройти заново» не нажимают: страшно потерять
          то, что уже получено. */}
      {screen.kind === "retake" ? (
        <div className="mx-auto mb-4 max-w-2xl rounded-2xl border border-yt-line bg-muted/40 px-4 py-3 text-sm">
          <p className="font-medium">Проходите анкету заново</p>
          <p className="mt-1 text-muted-foreground">
            Прежние ответы подставлены — меняйте только то, что изменилось. Отчёт, который у
            вас уже есть, остаётся на месте: новый появится рядом, когда вы дойдёте до конца.
          </p>
          <a
            className="mt-2 inline-block underline underline-offset-4"
            href={`/cabinet/edu-report?child=${childId}`}
          >
            Вернуться к отчёту, ничего не меняя
          </a>
        </div>
      ) : null}
      <EduQuestionnaire
        blocks={blocks}
        answers={answers}
        blockIndex={blockIndex}
        needsConsent={!state.data?.consentGranted}
        consentChecked={consentChecked}
        budgetSkipped={budgetSkipped}
        saving={save.isPending || consent.isPending}
        onConsentToggle={setConsentChecked}
        onConsentSubmit={() => consent.mutate()}
        onAnswer={(id, value: AnswerValue) => {
          setAnswers((prev) => ({ ...prev, [id]: value }));
        }}
        onNext={() => {
          // Каждый переход между блоками сохраняет черновик: закрытая вкладка
          // не должна стоить человеку заполненного блока.
          if (isLastBlock) {
            // Завершение анкеты — один из двух моментов, когда считается
            // подбор (второй — кнопка «Пересчитать» в отчёте). Ошибку расчёта
            // не показываем поверх экрана «готово»: анкета сохранена, а отчёт
            // можно посчитать с его собственной страницы.
            // Ошибка расчёта больше не глотается: она переводит экран в
            // отказ с кнопкой, а не оставляет обещание «меньше минуты».
            setJustCompleted(true);
            setAttempts(0);
            setGenerateFailed(false);
            endRetake();
            save.mutate(
              { complete: true },
              {
                onSuccess: () => {
                  makeReport({ data: { childId: childId as string } })
                    .then(() => report.refetch())
                    .catch(() => setGenerateFailed(true));
                },
                onError: () => setGenerateFailed(true),
              },
            );
            return;
          }
          save.mutate({});
          setBlockIndex((i) => i + 1);
        }}
        onBack={() => setBlockIndex((i) => Math.max(0, i - 1))}
        onSkipBlock={() => {
          // Пропуск — это ответ: пишем budget_skipped и идём дальше, ответы
          // блока денег не трогаем (их просто нет).
          setBudgetSkipped(true);
          save.mutate({ skipped: true });
          if (isLastBlock) {
            setJustCompleted(true);
            endRetake();
          } else setBlockIndex((i) => i + 1);
        }}
      />
    </div>
  );
}
